/**
 * ListFormFields - Shared form fields for list creation/editing
 *
 * Includes:
 * - Name input
 * - Icon picker
 * - Color picker
 * - Live preview
 */
import type { ReactElement } from 'react';

import ColorInput, { DEFAULT_PRESET_COLORS } from '@/components/ColorInput/ColorInput';
import IconSelector from '@/components/IconSelector/IconSelector';
import { DEFAULT_LIST_ICONS } from '@/types/lists';

// ============================================================================
// Types
// ============================================================================

export interface ListFormData {
  name: string;
  icon: string;
  color: string | null;
}

export interface ListFormFieldsProps {
  data: ListFormData;
  onChange: (data: ListFormData) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  showPreview?: boolean;
}

// Re-export for backwards compatibility
export { DEFAULT_PRESET_COLORS as PRESET_COLORS } from '@/components/ColorInput/ColorInput';

// ============================================================================
// Component
// ============================================================================

export default function ListFormFields({
  data,
  onChange,
  errors = {},
  disabled = false,
  showPreview = true,
}: ListFormFieldsProps): ReactElement {
  const handleNameChange = (name: string) => {
    onChange({ ...data, name });
  };

  const handleIconChange = (icon: string) => {
    onChange({ ...data, icon });
  };

  const handleColorChange = (color: string | null) => {
    onChange({ ...data, color });
  };

  return (
    <div className="space-y-5">
      {/* Name input */}
      <div>
        <label htmlFor="list-name" className="text-silver mb-1.5 block text-sm font-medium">
          Name <span className="text-danger">*</span>
        </label>
        <input
          id="list-name"
          type="text"
          value={data.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="e.g., Cards to Buy, Trade Targets..."
          className="border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-full rounded-lg border px-4 py-2.5 text-sm"
          autoFocus
          maxLength={50}
          disabled={disabled}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'list-name-error' : undefined}
        />
        {errors.name && (
          <p id="list-name-error" className="text-danger mt-1 text-xs">
            {errors.name}
          </p>
        )}
      </div>

      {/* Icon picker */}
      <IconSelector icons={DEFAULT_LIST_ICONS} value={data.icon} onChange={handleIconChange} label="Icon" disabled={disabled} />

      {/* Color picker */}
      <ColorInput
        value={data.color}
        onChange={handleColorChange}
        presets={DEFAULT_PRESET_COLORS}
        allowCustom={true}
        label="Color"
        optional
        disabled={disabled}
        id="list-color"
      />

      {/* Preview */}
      {showPreview && (
        <div className="bg-slate/50 rounded-lg p-3">
          <p className="text-ash mb-2 text-xs font-medium uppercase">Preview</p>
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-lg"
              style={data.color ? { backgroundColor: data.color } : { backgroundColor: 'var(--color-slate)' }}
            >
              {data.icon}
            </div>
            <span className="text-pearl font-medium">{data.name || 'List Name'}</span>
          </div>
        </div>
      )}
    </div>
  );
}
